import { UUID } from 'node:crypto';
import { Injectable } from '@angular/core';
import { ISelf } from '@shared/models/api/base';
import { CrudService } from '@shared/services/crud.service';
import { forkJoin, map, Observable, of, Subject, switchMap, tap } from 'rxjs';

/** Which day a shift is fetched on: the day it ends (backend default) or the day it starts. */
export type ShiftDateBasis = 'End' | 'Start';

export interface IShiftModel {
	id?: UUID | null;
	employeeId: UUID;
	rawStartTime: Date;
	rawEndTime: Date;
	correctedStartTime?: string | null;
	correctedEndTime?: string | null;
	validationErrors: IValidationErrors | null;
	approved: boolean;
	approvedBy?: UUID | null;
	validatedAt?: string | null;
	timestamps: ITimestamp[];
}

export interface IValidationErrors {
	arrivedEarlyValidator?: IArrivedEarlyValidator;
	arrivedLateValidator?: IArrivedLateValidator;
	leftEarlyValidator?: ILeftEarlyValidator;
	leftLateValidator?: ILeftLateValidator;
	noShiftOnDateValidator?: INoShiftOnDateValidator;
	notAWorkdayValidator?: INotAWorkdayValidator;
	shiftNotEndedValidator?: IShiftNotEndedValidator;
	timeOnShiftExceedsWorkTimeValidator?: ITimeOnShiftExceedsWorkTime;
	timeOnShiftInsufficientValidator?: ITimeOnShiftInsufficient;
}

export interface ITimestamp {
	id?: UUID | null;
	workTaskId: UUID;
	departmentId?: UUID | null;
	employeeId?: UUID | null;
	shiftId?: UUID | null;
	teamProfileId?: UUID | null;
	dateTimeOffset: string;
	timestampStatus?: string;
}

export interface IShiftMergeRequest {
	shiftIds: UUID[];
	gapWorkTaskId: UUID | null;
}

export interface IShiftPostRequest {
	employeeId: string;
	correctedStartTime: string;
	correctedEndTime: string;
	timestamps: ITimestamp[];
}

export interface IArrivedEarlyValidator {
	actualStartTime: string; // 2024-11-25T21:00:00+01:00
	expectedStartTime: string; // 2024-11-25T20:00:00+01:00
}

export interface IArrivedLateValidator {
	actualStartTime: string; // 2024-11-25T21:00:00+01:00
	expectedStartTime: string; // 2024-11-25T20:00:00+01:00
}

export interface ILeftEarlyValidator {
	actualEndTime: string; // 2024-11-26T05:00:00+01:00
	expectedEndTime: string; // 2024-11-25T04:00:00+01:00
}

export interface ILeftLateValidator {
	actualEndTime: string; // 2024-11-26T05:00:00+01:00
	expectedEndTime: string; // 2024-11-25T04:00:00+01:00
}

export interface INotAWorkdayValidator {
	dayOfWeek: string; // Tuesday
}

export interface IShiftNotEndedValidator {}

export interface ITimeOnShiftExceedsWorkTime {
	actualWorkTime: string;
	expectedWorkTime: string;
}

export interface ITimeOnShiftInsufficient {
	actualWorkTime: string;
	expectedWorkTime: string;
}

export interface INoShiftOnDateValidator {
	expectedStart: string; // 2024-11-01T08:00:00+01:00
	expectedEnd: string; // 2024-11-01T15:00:00+01:00
}

interface IShiftPutRequest extends ISelf {
	correctedStartTime: string;
	correctedEndTime: string;
	timestamps: ITimestamp[];
}

interface IShiftPatchRequest extends ISelf {
	approved: boolean;
}

interface IResultResponse<ResponseType> {
	result: ResponseType;
}

@Injectable({
	providedIn: 'root',
})
export class MesShiftService {
	constructor(private coreClient: CrudService) {}

	private eventSubjectCreate = new Subject<IShiftModel>();
	observeCreated(): Observable<IShiftModel> {
		return this.eventSubjectCreate.asObservable();
	}

	private eventSubjectUpdate = new Subject<IShiftModel>();
	observeUpdated(): Observable<IShiftModel> {
		return this.eventSubjectUpdate.asObservable();
	}

	private eventSubjectPatch = new Subject<IShiftModel>();
	observePatched(): Observable<IShiftModel> {
		return this.eventSubjectPatch.asObservable();
	}

	private eventSubjectDelete = new Subject<IShiftModel | null>();
	observeDeleted(): Observable<IShiftModel | null> {
		return this.eventSubjectDelete.asObservable();
	}

	getByDepartmentAndDate(departmentId: UUID, startDate: string, days = 0, dateBasis: ShiftDateBasis = 'End'): Observable<IShiftModel[]> {
		const r = this.coreClient.get<IResultResponse<IShiftModel[]>>(`/mes/departments/${departmentId}/shifts?startDate=${startDate}&days=${days}&dateBasis=${dateBasis}`).pipe(map((d) => d.result));
		return r;
	}

	getByDepartmentsAndDate(departmentIds: UUID[], startDate: string, days = 0, dateBasis: ShiftDateBasis = 'End'): Observable<IShiftModel[]> {
		if (departmentIds.length === 0) return of([]);
		return forkJoin(departmentIds.map((departmentId) => this.getByDepartmentAndDate(departmentId, startDate, days, dateBasis))).pipe(map((results) => results.flat()));
	}

	createShift(shift: IShiftModel): Observable<IResultResponse<IShiftModel>> {
		const request: IShiftPostRequest = {
			employeeId: shift.employeeId,
			correctedStartTime: shift.correctedStartTime,
			correctedEndTime: shift.correctedEndTime,
			timestamps: shift.timestamps,
		};

		return this.coreClient.create<IShiftPostRequest, IResultResponse<IShiftModel>>(request, '/mes/shifts').pipe(tap((response) => this.eventSubjectCreate.next(response.result)));
	}

	mergeShifts(shiftIds: UUID[], gapWorkTaskId: UUID | null): Observable<IResultResponse<IShiftModel>> {
		const request: IShiftMergeRequest = { shiftIds, gapWorkTaskId };

		return this.coreClient.create<IShiftMergeRequest, IResultResponse<IShiftModel>>(request, '/mes/shifts/merge');
	}

	updateShift(shift: IShiftModel): Observable<IResultResponse<IShiftModel>> {
		const placeholder = !shift.id;
		return (placeholder ? this.createShift(shift).pipe(map((r) => r.result)) : of(shift)).pipe(
			switchMap((s) => {
				const request: IShiftPutRequest = {
					self: `/mes/shifts/${s.id}`,
					correctedStartTime: shift.correctedStartTime,
					correctedEndTime: shift.correctedEndTime,
					timestamps: shift.timestamps,
				};
				return this.coreClient.update<IResultResponse<IShiftModel>>(request);
			}),
			tap((response) => this.eventSubjectUpdate.next(response.result)),
		);
	}

	patchShift(shift: IShiftModel): Observable<IResultResponse<IShiftModel>> {
		const placeholder = !shift.id;
		return (placeholder ? this.createShift(shift).pipe(map((r) => r.result)) : of(shift)).pipe(
			switchMap((s) => {
				s.approved = shift.approved;
				const request: IShiftPatchRequest = {
					self: `/mes/shifts/${s.id}/approve`,
					approved: s.approved,
				};
				return this.coreClient.patch<IResultResponse<IShiftModel>>(request);
			}),
			tap((response) => this.eventSubjectPatch.next(response.result)),
		);
	}

	deleteShift(shift: IShiftModel): Observable<any> {
		return this.coreClient.delete(`/mes/shifts/${shift.id}`).pipe(tap(() => this.eventSubjectDelete.next(shift)));
	}
}
